import { useContext, CSSProperties, useEffect, useState } from 'react';
import { Tag, TagProps } from '../Tag/Tag';
import { EnvironmentContext } from '../../contexts';
import { WithOrWithoutLink } from '../WithOrWithoutLink/WithOrWithoutLink';
import { Link, LinkProps } from '../Link/Link';
import { RestrictionLevel } from '../MembershipTypeLogos/constants';
import MembershipTypesLogos from '../MembershipTypeLogos/MembershipTypesLogos';
import { useScreenDetector, useSvgFromUrl } from '../../hooks';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Elements } from '@kontent-ai/delivery-sdk';
import RichText from '../RichText/RichText';
import {
  hasValue,
  getProjectType,
  ProjectTypes,
} from '@r-and-a-shared-ui/utils';

import stylesPP from './scss/CallOutPanelPP.module.scss';
import stylesMMB from './scss/CallOutPanelMMB.module.scss';

type ImageType = {
  url: string;
  alt: string | '';
};

export interface CallOutPanelProps {
  backgroundImage?: string;
  backgroundColor?: string;
  mobileBackgroundImage?: string;
  description?: Elements.RichTextElement;
  title?: string;
  tags?: TagProps[];
  link?: LinkProps;
  access?: keyof typeof RestrictionLevel;
  buttonTitle?: string;
  icon?: ImageType;
  theme?: string;
  kontentItemId?: string;
}

export function CallOutPanel(props: CallOutPanelProps) {
  const { Image } = useContext(EnvironmentContext);
  const { isMobile, isTablet } = useScreenDetector();

  const {
    backgroundImage,
    backgroundColor,
    mobileBackgroundImage,
    description,
    title,
    tags,
    link,
    access = 'none',
    buttonTitle,
    icon,
    theme,
    kontentItemId,
  } = props;

  const projectType = getProjectType();
  const isPPproject = projectType === ProjectTypes.PlayerPortals;
  const styles = isPPproject ? stylesPP : stylesMMB;

  const inlineStyle: CSSProperties = {
    backgroundColor,
    ...(backgroundImage &&
      isPPproject && {
        backgroundImage: `url(${backgroundImage})`,
      }),
  };

  const imageSource =
    isMobile && mobileBackgroundImage ? mobileBackgroundImage : backgroundImage;

  const isHoverable = hasValue(link?.href);

  const formattedDescription = isPPproject ? description : description?.value;

  const ctaLink = {
    title: buttonTitle || '',
    internal: !!link?.internal,
    openInNewTab: !!link?.openInNewTab,
    href: link?.href || '',
  };

  const svgContent = useSvgFromUrl(icon?.url, styles['cardIconSvg']);

  return (
    <div
      className={`${styles['wrapper']} ${
        icon?.url ? styles['wrapper--withIcon'] : ''
      }
      ${backgroundImage ? styles['wrapper--withImgBg'] : ''} ${
        theme ? styles[theme] : styles['light']
      }
       ${isHoverable && !isPPproject ? styles['hoverable'] : ''}`}
      style={inlineStyle}
      data-kontent-item-id={kontentItemId}
    >
      <WithOrWithoutLink
        url={
          ((isMobile || isTablet) && buttonTitle) || isPPproject
            ? undefined
            : link
        }
      >
        {!isPPproject && imageSource && (
          <div className={styles['background']}>
            <Image
              src={imageSource}
              alt={'Call out panel image'}
              width={372}
              height={250}
            />
          </div>
        )}

        {!isPPproject && (
          <div className={styles['labels-container']}>
            <MembershipTypesLogos
              restrictionLevel={access}
              className={styles['logo']}
            />
            {tags && (
              <div className={styles['tags']}>
                {tags?.map((tag, i) => (
                  <Tag
                    key={`${i}`}
                    label={tag.label}
                    backgroundColor={tag?.backgroundColor}
                    textColor={tag?.textColor}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {icon && icon.url && (
          <>
            <span className={styles['cardIconBg']}></span>

            <span className={styles['cardIcon']}>
              <span dangerouslySetInnerHTML={{ __html: svgContent! }} />
            </span>
          </>
        )}

        <div className={styles['content']}>
          {title && <h4 className={styles.title}>{title}</h4>}

          {isPPproject ? (
            <RichText
              content={formattedDescription as Elements.RichTextElement}
            />
          ) : (
            formattedDescription && (
              <div
                dangerouslySetInnerHTML={{
                  __html: formattedDescription as string,
                }}
                className={styles['description']}
              />
            )
          )}
          {(isMobile || isTablet) &&
          !isPPproject &&
          ctaLink.title.length &&
          ctaLink.href.length ? (
            <Link
              className={classNames(styles.btn, styles['btn--secondary'], {
                [styles['inactive-link']]: !ctaLink?.href?.length,
              })}
              {...ctaLink}
            />
          ) : null}
        </div>
        {isPPproject && (
          <>
            {<div className={styles['bgMask']} />}

            {hasValue(link) && hasValue(link?.href) && (
              <>
                <span className={styles['linkIconBg']}></span>
                <Link hideIcon {...link!}>
                  <span className={styles['linkIcon']}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                </Link>
              </>
            )}
          </>
        )}
      </WithOrWithoutLink>
    </div>
  );
}

export default CallOutPanel;
